import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
    const [params] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const phase = params.get("phase");
    const isCancel = location.pathname.includes("cancel");
    const [state, setState] = useState(isCancel ? "cancelled" : "checking");
    const tries = useRef(0);

    useEffect(() => {
        if (isCancel) return;
        if (!sessionId) { setState("failed"); return; }
        let active = true;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (!active) return;
                if (data.payment_status === "paid") { setState("paid"); return; }
                if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
            } catch (e) { /* riprova */ }
            if (!active) return;
            tries.current += 1;
            if (tries.current >= 8) { setState("timeout"); return; }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { active = false; };
    }, [sessionId, isCancel]);

    const content = {
        checking: { icon: <Loader2 className="h-12 w-12 text-terracotta animate-spin" />, title: "Verifica del pagamento…", text: "Attendi qualche istante, stiamo confermando la transazione." },
        paid: {
            icon: <CheckCircle2 className="h-12 w-12 text-emerald-600" />,
            title: phase === "deposit" ? "Acconto versato!" : "Pagamento riuscito!",
            text: phase === "deposit"
                ? "Il tuo acconto è stato registrato. Potrai pagare il saldo in seguito dalle tue prenotazioni."
                : "Il pagamento è stato registrato in garanzia fino al completamento del lavoro. Grazie!",
        },
        cancelled: { icon: <XCircle className="h-12 w-12 text-ink/40" />, title: "Pagamento annullato", text: "Nessun addebito è stato effettuato. Puoi riprovare quando vuoi." },
        failed: { icon: <XCircle className="h-12 w-12 text-rose-500" />, title: "Pagamento non riuscito", text: "Qualcosa è andato storto con il pagamento. Riprova dalla dashboard." },
        timeout: { icon: <Loader2 className="h-12 w-12 text-amber-500" />, title: "Elaborazione in corso", text: "Il pagamento è ancora in elaborazione. Controlla tra poco lo stato dalle tue prenotazioni." },
    }[state];

    return (
        <div className="min-h-screen flex items-center justify-center bg-cream px-5">
            <div className="max-w-md w-full rounded-2xl border border-border bg-white p-8 text-center" data-testid="payment-return">
                <div className="flex justify-center mb-4">{content.icon}</div>
                <h1 className="font-heading text-2xl font-bold text-ink" data-testid="payment-return-title">{content.title}</h1>
                <p className="mt-2 text-ink/60">{content.text}</p>
                <Button onClick={() => navigate("/cliente")} className="mt-6 rounded-full bg-terracotta hover:bg-terracottahover" data-testid="payment-return-dashboard">
                    Torna alle prenotazioni
                </Button>
            </div>
        </div>
    );
}
